import { useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import RoundChatIcon from '../../../view/components/icons/RoundChatIcon'
import MyTaskIcon from '../../../assets/my_task_icon.png'
import { intl } from '../../../i18n'
import { AllState } from '../../../store'
import { demoUser } from '../../data/tenants/tenant'
import { colorPalette } from '../../../view/style/colorPalette'
import { Color, TextColor, FontWeight } from '../../../styles/commonStyles'
import { Title, Text } from '../../../view/components/typography'
import { BootstrapTooltip } from '../../../view/components/tooltips/Tooltip/BootStrapToopTip'
import { SUPPORT_SITE_TOP_URL } from '../support-site-guide/paths'
import { DemoDisabledAction } from './DemoDisabledAction'
import { DemoLimitationNotice } from './DemoLimitationNotice'

// 本物のUserInformationHeader（view/containers/UserInformationHeader）は
// プロジェクト切替・通知・検索・ユーザーメニュー等がすべてRedux state/API呼び出し
// に依存するため、そのままでは使用できない。見た目（アイコン構成・配色・レイアウト）
// のみ揃えた静的な再現とし、操作をDemoDisabledActionで無効化する。
// headerはクリック時のSnackbar通知を出さず、ホバー時のTooltipのみで伝える方針
// のため、disableClickをtrueにしている。
// 例外はヘルプアイコンで、遷移先（サポートサイト）がデモの外にあり
// モックを必要としないため、無効化せず実際に遷移させる。
//
// タイトルは本物のTitle（state.appFunction.functionsをexternalIdで検索）を
// 模倣する代わりに、useRegisterDemoFunctionLayerが各デモ画面から登録する
// state.functionLayer.layers.get(0).titleをそのまま表示する。ルートパスごとの
// 分岐をここに持たないため、デモ画面が増えてもこのファイルは変更不要。
//
// タイマー（PunchClockIcon）は本物のヘッダーに並ぶが、デモでは再現しない。
// タイマーはstart/stopを実際に計測して初めて意味を持つ機能で、その
// APIをモックしていないため、置いても押せないアイコンが増えるだけになる。
// マイタスク画面のタイマー列も同じ理由で出さない（そちらは製品コードの
// featureFlagがalias判定で落とすので、useSeedDemoTenant.tsが
// state.tenant.organizationに'demo'を積むことで無効になる）。
const Container = styled('header')({
  height: '56px',
  width: '100%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  // タイトルと案内が並ぶ左側は、右のアクション群を押し出さないよう
  // 必要に応じて縮める（案内側で文言が省略される）。
  minWidth: 0,
})

// タイトルと制限事項の案内を1つのまとまりとして左端に置く。
const TitleArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '20px',
  minWidth: 0,
})

// 案内が伸びてもタイトルが省略されないようにする。
const TitleLabel = styled(Title)({
  flexShrink: 0,
})

const ActionsArea = styled('div')({
  display: 'flex',
  margin: '0 0 0 auto',
  alignItems: 'center',
})

const MyTaskButton = styled(Button)({
  borderRadius: '4px',
  height: '28px',
  width: '98px',
  background: colorPalette.skyBlue[7],
  boxShadow: 'none',
  margin: '0 0 0 10px',
  padding: '0 6px',
  border: 'none',
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[5],
    boxShadow: 'none',
    border: 'none',
  },
})

// ヘルプだけはデモでも操作できる。本物のヘッダーはhelp-drawerフラグで
// 分岐するが、デモのデプロイ環境はFLAGXS_ENV=productionで同フラグがoffのため、
// 本番と同じ「サポートサイトを別タブで開く」挙動（とTooltip文言）に揃える。
// 本物のようにフラグを参照しないのは、デモの実環境では常にoff側に決まるため。
// 遷移はwindow.openを使わずaタグで行う（demoInquiry.ts参照）。
const HelpLink = styled('a')({
  background: 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '6px',
  margin: '0 35px 0 16px',
})

const UserArea = styled('div')({
  width: '240px',
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  borderLeft: `1px solid ${colorPalette.monotone[2]}`,
  justifyContent: 'center',
})

export const DemoUserInformationHeader = () => {
  const title = useSelector(
    (state: AllState) => state.functionLayer.layers.get(0)?.title
  )

  return (
    <Container>
      <TitleArea>
        <TitleLabel variant="s">{title}</TitleLabel>
        <DemoLimitationNotice />
      </TitleArea>
      <ActionsArea>
        <DemoDisabledAction disableClick={true}>
          <IconButton sx={{ padding: '6px', margin: '0 5px 0 25px' }}>
            <RoundChatIcon
              style={{
                height: '17px',
                verticalAlign: 'middle',
                color: Color.MONOTONE,
                fill: 'none',
              }}
            />
          </IconButton>
        </DemoDisabledAction>
        <DemoDisabledAction disableClick={true}>
          <IconButton sx={{ padding: '6px', marginRight: '10px' }}>
            <NotificationsRoundedIcon
              sx={{ fontSize: 20, color: Color.MONOTONE }}
            />
          </IconButton>
        </DemoDisabledAction>
        <DemoDisabledAction disableClick={true}>
          <MyTaskButton variant="outlined">
            <img
              style={{ padding: '3px', height: '20px', width: '20px' }}
              src={MyTaskIcon}
              alt=""
            />
            <Text
              variant="s"
              color={TextColor.WHITE}
              style={{
                whiteSpace: 'nowrap',
                textTransform: 'lowercase',
                overflow: 'hidden',
                padding: '0 4px',
              }}
            >
              {intl.formatMessage({ id: 'menu.myTask' })}
            </Text>
          </MyTaskButton>
        </DemoDisabledAction>
        <BootstrapTooltip
          title={intl.formatMessage({ id: 'login.supportSite' })}
        >
          <HelpLink
            href={SUPPORT_SITE_TOP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <HelpOutlineRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
          </HelpLink>
        </BootstrapTooltip>
      </ActionsArea>
      <UserArea>
        <Avatar
          src={demoUser.iconUrl}
          variant="circular"
          sx={{
            height: '32px',
            width: '32px',
            margin: '10px',
            border: `1px solid ${colorPalette.monotone[2]}`,
          }}
        />
        <Text
          variant="2sBold"
          color={colorPalette.monotone[10]}
          style={{
            marginRight: '10px',
            width: '100px',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            fontWeight: FontWeight.BOLD,
          }}
        >
          {demoUser.name}
        </Text>
        <DemoDisabledAction disableClick={true}>
          <span style={{ cursor: 'pointer', display: 'flex' }}>
            <ExpandMoreRounded sx={{ color: Color.MONOTONE }} />
          </span>
        </DemoDisabledAction>
      </UserArea>
    </Container>
  )
}
